import { cn } from "../foundations/cn.js";
import { formatChance } from "./odds.js";
import { RARITY_FILL, RARITY_LABELS, type RarityTier } from "./rarity.js";

export interface OddsBarProps {
  /** Each tier's total chance in percent, in tier order. Tiers with 0 are left out. */
  tiers: readonly { tier: RarityTier; chance: number; label?: string }[];
  /** The bar's name, e.g. "Odds by tier". */
  label?: string;
  className?: string;
}

/** A box's published odds by tier: a proportional bar and a mono legend. Never animated. */
export function OddsBar({ tiers, label = "Odds by tier", className }: OddsBarProps) {
  const shown = tiers.filter((t) => t.chance > 0);
  const total = shown.reduce((sum, t) => sum + t.chance, 0) || 1;
  return (
    <figure className={cn("flex flex-col gap-2", className)}>
      <div aria-hidden className="flex h-1.5 w-full gap-0.5 overflow-hidden rounded-pill">
        {shown.map((t) => (
          <span
            key={t.tier}
            className={cn("h-full min-w-1 rounded-pill", RARITY_FILL[t.tier])}
            style={{ flexGrow: t.chance / total }}
          />
        ))}
      </div>
      <figcaption className="sr-only">{label}</figcaption>
      <ul className="flex flex-wrap gap-x-4 gap-y-1">
        {shown.map((t) => (
          <li
            key={t.tier}
            className="inline-flex items-center gap-1.5 font-mono text-mono-meta text-text-secondary tabular-nums"
          >
            <span aria-hidden className={cn("size-1.5 rounded-pill", RARITY_FILL[t.tier])} />
            {t.label ?? RARITY_LABELS[t.tier]} {formatChance(t.chance)}
          </li>
        ))}
      </ul>
    </figure>
  );
}
